import { useEffect, useMemo, useRef } from 'react';
import { useEstadosLote } from '@/hooks/useFases';
import { X } from '@phosphor-icons/react';
import type { EstadoLote, Fase, LoteKanban } from '@/types';
import { contrastingText } from '@/lib/utils';

interface Props {
  lote: LoteKanban;
  fase: Fase;
  // Coordenadas del drop. La UI posiciona el popover cerca del puntero
  // (con clamp al viewport para que no se salga del lado derecho/abajo).
  x: number;
  y: number;
  // Filtra los estados al tipo de proyecto activo (ej. 'Permiso Vivienda')
  // para que no aparezcan los de APC EN cuando esa rama no aplica.
  tipoProyecto?: string;
  onPick: (estado: EstadoLote) => void;
  onClose: () => void;
}

// Popover con la lista de estados de una fase. Aparece cuando el usuario
// suelta un lote en una columna y muestra solo los estados de esa fase.
// Si la fase no tiene estados (caso degradado: catalogo incompleto),
// muestra un mensaje vacio y solo permite cerrar.
export function EstadoPickerDialog({ lote, fase, x, y, tipoProyecto, onPick, onClose }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { data: estados = [], isLoading } = useEstadosLote({ idFase: fase.idFase, tipoProyecto });

  // Cerrar con ESC y click fuera.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  // Agrupar por Etapa para que un listado largo (ej. APC CFIA con 12+
  // estados) quede legible. Mantiene el orden de la primera aparicion.
  const grouped = useMemo(() => {
    const out: Array<{ etapa: string; items: EstadoLote[] }> = [];
    const byEtapa = new Map<string, EstadoLote[]>();
    for (const e of estados) {
      if (!byEtapa.has(e.etapa)) {
        byEtapa.set(e.etapa, []);
        out.push({ etapa: e.etapa, items: byEtapa.get(e.etapa)! });
      }
      byEtapa.get(e.etapa)!.push(e);
    }
    return out;
  }, [estados]);

  // Clamp al viewport para no recortar el popover. Asume ancho ~320 / alto ~360.
  const left = Math.min(x, window.innerWidth - 340);
  const top = Math.min(y, window.innerHeight - 380);
  const faseColor = fase.colorHex || '#888780';
  const headerText = contrastingText(faseColor);

  return (
    <div
      ref={ref}
      className="fixed z-50 rounded-md shadow-xl bg-white border border-stone-200 w-[320px] max-h-[360px] flex flex-col"
      style={{ left: Math.max(8, left), top: Math.max(8, top) }}
      role="dialog"
      aria-label={`Estados de la fase ${fase.nombre}`}
    >
      <div
        className="flex items-center justify-between px-3 py-2 rounded-t-md"
        style={{ background: faseColor, color: headerText }}
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase opacity-80">Mover a</div>
          <div className="font-semibold text-sm truncate">{fase.nombre}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-black/10"
          aria-label="Cerrar"
        >
          <X size={14} weight="bold" />
        </button>
      </div>

      <div className="px-3 py-1.5 text-[11px] text-stone-500 border-b border-stone-200 truncate">
        Lote {lote.idBd || lote.lote || lote.idLote}
      </div>

      <div className="flex-1 overflow-y-auto p-1.5">
        {isLoading ? (
          <div className="text-xs text-stone-400 text-center py-6">Cargando estados…</div>
        ) : estados.length === 0 ? (
          <div className="text-xs text-stone-400 text-center py-6 italic">
            Esta fase no tiene estados configurados.
          </div>
        ) : (
          grouped.map(group => (
            <div key={group.etapa} className="mb-1.5 last:mb-0">
              <div className="text-[10px] uppercase text-stone-400 font-semibold px-2 py-1">
                {group.etapa}
              </div>
              {group.items.map(e => {
                const isCurrent = lote.idEstadoLoteActual === e.idEstadoLote;
                const dotColor = e.colorHex || faseColor;
                return (
                  <button
                    key={e.idEstadoLote}
                    type="button"
                    onClick={() => onPick(e)}
                    className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-start gap-2 hover:bg-stone-100 ${
                      isCurrent ? 'bg-stone-50 ring-1 ring-brand-green-100' : ''
                    }`}
                    title={e.descripcion ?? undefined}
                  >
                    <span
                      className="mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: dotColor }}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="font-medium text-stone-800 block truncate">
                        {e.estado}
                        {e.tgCaminoFeliz && (
                          <span className="ml-1.5 text-[9px] text-brand-green-200">●</span>
                        )}
                      </span>
                      {e.descripcion && (
                        <span className="text-[10px] text-stone-500 block truncate">
                          {e.descripcion}
                        </span>
                      )}
                    </span>
                    {isCurrent && (
                      <span className="text-[9px] text-brand-green-200 font-bold uppercase flex-shrink-0 mt-1">
                        actual
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
